import React, { useState, useEffect } from 'react';
import { Briefcase, MapPin, Clock, DollarSign, Building2, Search, Plus, Trash2, Lock, Eye, EyeOff, X, Filter } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

const EmpregosJoacaba = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    company: '',
    category: 'Sem categoria',
    salary: '',
    workSchedule: '',
    description: '',
    requirements: '',
    benefits: '',
    contact: '',
    location: 'Joaçaba, SC'
  });

  const categories = [
    'Sem categoria',
    'Administrativo',
    'Comercial/Vendas',
    'Operacional',
    'Saúde e Beleza',
    'Alimentação',
    'Tecnologia',
    'Educação',
    'Construção',
    'Transporte/Logística',
    'Outros'
  ];

  useEffect(() => {
    // Função async para buscar as vagas
    const getJobs = async () => {
      // .from('vagas') -> nome da sua tabela
      // .select('*') -> pega todas as colunas
      // .order('created_at', { ascending: false }) -> mais novas primeiro
      let { data: vagas, error } = await supabase
        .from('vagas')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar vagas:', error);
      } else {
        setJobs(vagas);
      }
    };

    getJobs(); // Chama a função

    // Mantém a lógica do admin
    const adminStatus = sessionStorage.getItem('joacaba-admin');
    if (adminStatus === 'true') {
      setIsAdmin(true);
    }
  }, []);

  useEffect(() => {
    let filtered = jobs;

    if (searchTerm) {
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(job => job.category === categoryFilter);
    }

    setFilteredJobs(filtered);
  }, [jobs, searchTerm, categoryFilter]);

  const handleSubmit = async () => { // Virou async
    if (!formData.title || !formData.company || !formData.description || !formData.contact) {
      alert('Por favor, preencha todos os campos obrigatórios (*)');
      return;
    }

    // O formData já tem todos os campos corretos para o DB
    const { data, error } = await supabase
      .from('vagas')
      .insert([formData]) // Salva o novo job (formData) no banco
      .select(); // Retorna o job que acabou de ser salvo

    if (error) {
      console.error('Erro ao cadastrar vaga:', error);
      alert('Erro ao cadastrar vaga. Tente novamente.');
    } else {
      // Adiciona a nova vaga (retornada do DB) no início da lista
      setJobs([data[0], ...jobs]);

      // Limpa o formulário
      setFormData({
        title: '',
        company: '',
        category: 'Sem categoria',
        salary: '',
        workSchedule: '',
        description: '',
        requirements: '',
        benefits: '',
        contact: '',
        location: 'Joaçaba, SC'
      });
      setShowForm(false);
      alert('Vaga cadastrada com sucesso!');
    }
  };
  const handleDelete = async (id) => { // Virou async
    if (window.confirm('Tem certeza que deseja excluir esta vaga?')) {

      const { error } = await supabase
        .from('vagas')
        .delete()
        .match({ id: id }); // Deleta a vaga onde o 'id' bate

      if (error) {
        console.error('Erro ao deletar vaga:', error);
        alert('Erro ao deletar vaga.');
      } else {
        // Remove a vaga da lista no estado local (após sucesso no DB)
        const updatedJobs = jobs.filter(job => job.id !== id);
        setJobs(updatedJobs);
      }
    }
  };

  const handleLogin = () => {
    if (adminPassword === process.env.REACT_APP_ADMIN_PASSWORD) {
      setIsAdmin(true);
      sessionStorage.setItem('joacaba-admin', 'true');
      setShowLogin(false);
      setAdminPassword('');
    } else {
      alert('Senha incorreta!');
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    sessionStorage.removeItem('joacaba-admin');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-lg">
                <Briefcase className="text-white" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Empregos Joaçaba</h1>
                <p className="text-sm text-gray-600">Conectando talentos e oportunidades</p>
              </div>
            </div>
            <div className="flex gap-2">
              {!isAdmin ? (
                <button
                  onClick={() => setShowLogin(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  <Lock size={18} />
                  Admin
                </button>
              ) : (
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Sair Admin
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Search and Filter Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar vagas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="pl-10 pr-8 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">Todas Categorias</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition shadow-md"
            >
              <Plus size={20} />
              Cadastrar Vaga
            </button>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>{filteredJobs.length} vaga(s) encontrada(s)</span>
            {isAdmin && <span className="text-green-600 font-semibold">Modo Administrador Ativo</span>}
          </div>
        </div>

        {/* Jobs List */}
        <div className="grid md:grid-cols-2 gap-6">
          {filteredJobs.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <Briefcase className="mx-auto mb-4 text-gray-400" size={64} />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Nenhuma vaga encontrada</h3>
              <p className="text-gray-500">Seja o primeiro a cadastrar uma oportunidade!</p>
            </div>
          ) : (
            filteredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {job.category}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(job.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">{job.title}</h3>
                    <div className="flex items-center gap-2 text-gray-600 mb-3">
                      <Building2 size={18} />
                      <span className="font-medium">{job.company}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(job.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  {job.salary && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <DollarSign size={18} className="text-green-600" />
                      <span>{job.salary}</span>
                    </div>
                  )}
                  {job.workSchedule && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Clock size={18} className="text-blue-600" />
                      <span>{job.workSchedule}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-700">
                    <MapPin size={18} className="text-red-600" />
                    <span>{job.location}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Descrição:</h4>
                    <p className="text-gray-600">{job.description}</p>
                  </div>

                  {job.requirements && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">Requisitos:</h4>
                      <p className="text-gray-600">{job.requirements}</p>
                    </div>
                  )}

                  {job.benefits && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">Benefícios:</h4>
                      <p className="text-gray-600">{job.benefits}</p>
                    </div>
                  )}

                  <div className="pt-3 border-t">
                    <h4 className="font-semibold text-gray-800 mb-1">Contato:</h4>
                    <p className="text-blue-600">{job.contact}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Job Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Cadastrar Nova Vaga</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Cargo/Título da Vaga *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Auxiliar de Depósito"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Empresa *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nome da empresa"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Categoria *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Salário</label>
                  <input
                    type="text"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: R$ 2.000,00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Horário de Trabalho</label>
                  <input
                    type="text"
                    value={formData.workSchedule}
                    onChange={(e) => setFormData({ ...formData, workSchedule: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Segunda a Sexta, 8h às 18h"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Descrição da Vaga *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Descreva as principais atividades e responsabilidades"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Requisitos</label>
                <textarea
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Formação, experiência, habilidades necessárias"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Benefícios</label>
                <textarea
                  value={formData.benefits}
                  onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="2"
                  placeholder="Vale transporte, vale refeição, plano de saúde, etc"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Contato *</label>
                <input
                  type="text"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="WhatsApp, e-mail ou telefone"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition"
                >
                  Cadastrar Vaga
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Login Administrativo</h2>
              <button onClick={() => setShowLogin(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Senha Admin</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                    placeholder="Digite a senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Senha padrão: admin123</p>
              </div>

              <button
                type="button"
                onClick={handleLogin}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition"
              >
                Entrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-800 text-white mt-12 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-2">© 2025 Empregos Joaçaba - Todos os direitos reservados</p>
          <p className="text-sm text-gray-400">Conectando talentos e oportunidades em Joaçaba, SC</p>
        </div>
      </footer>
    </div>
  );
};

export default EmpregosJoacaba;